import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  type ConnectedPosition,
} from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { PaperIconComponent, PaperInputComponent } from '@ricoh-paper/angular';
import { ChromeService } from './chrome.service';
import { DIVISION_SEARCH_THRESHOLD, filterDivisions } from './division-filter';
import { ALL_DIVISIONS_ID, type Division } from '../core/constants/divisions';
import { AnalyticsService } from '../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../core/analytics/events';

const POSITIONS: ConnectedPosition[] = [
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 10 },
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 10 },
  { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -10 },
];

/**
 * The global division switcher, in the workspace nav's end cluster.
 *
 * WHAT IT IS. Settings' scope is the DIVISION (AdminScopeService: one
 * division, or "All divisions" for an admin of the whole estate), read by
 * every estate page. One Paper keeps that model (A4 brief § Scope) and gives
 * it the shell's look: the nav's frost chip, and a panel drawn like
 * `paper-location-scope`'s — a solid reading surface, a section head on a
 * hairline, rows with a tile, one selected state (a 12% ink wash, a tick,
 * weight 500). "All divisions" leads rather than trailing, as the location
 * panel's "All locations" does: it is an estate admin's usual scope, and a
 * deep division hierarchy would push it out of view.
 *
 * WHY NOT `paper-location-scope` ITSELF. Its words, sections and model are
 * locations (Your sites, Groups, All locations, a `LocationScope`), and
 * whether Settings also needs a location scope beside the division one is an
 * open question for Nath. A matching chip and panel keeps the division model
 * honest without bending the location component's copy.
 *
 * The division's ink is DATA here (the swatch), never the chip's or the
 * panel's paint: the colour the app wears is the person's accent.
 */
@Component({
  selector: 'ws-division-switcher',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    NgTemplateOutlet,
    FormsModule,
    PaperIconComponent,
    PaperInputComponent,
    TranslocoPipe,
  ],
  template: `
    <button
      #trigger
      type="button"
      class="chip"
      cdkOverlayOrigin
      #origin="cdkOverlayOrigin"
      aria-haspopup="dialog"
      data-testid="division-switcher"
      [attr.aria-expanded]="open()"
      [attr.aria-label]="'shell.division_switcher.chip_aria' | transloco: { division: active().name }"
      (click)="toggle()"
    >
      <span class="swatch" aria-hidden="true" [style.background]="active().swatch"></span>
      <span class="chip__name">{{ active().name }}</span>
      <paper-icon class="chip__chevron" name="chevron-down" [size]="14" />
    </button>

    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="origin"
      [cdkConnectedOverlayOpen]="open()"
      [cdkConnectedOverlayPositions]="positions"
      [cdkConnectedOverlayHasBackdrop]="true"
      cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
      cdkConnectedOverlayPanelClass="ws-division-switcher-pane"
      [cdkConnectedOverlayPush]="true"
      [cdkConnectedOverlayViewportMargin]="12"
      (backdropClick)="close()"
      (detach)="close(false)"
      (attach)="onAttach()"
      (overlayKeydown)="onOverlayKeydown($event)"
    >
      <div
        #panel
        class="panel"
        role="dialog"
        tabindex="-1"
        [attr.aria-label]="'shell.division_switcher.dialog' | transloco"
      >
        @if (named().length === 0 && !allRow()) {
          <p class="state" role="status">{{ 'shell.division_switcher.loading' | transloco }}</p>
        } @else {
          @if (allRow(); as all) {
            <!-- "All divisions" leads: it is an estate admin's usual scope, and
                 a hierarchy of fifteen divisions would push it out of view. -->
            <div class="list list--all" role="radiogroup" [attr.aria-label]="'shell.division_switcher.all' | transloco">
              <ng-container *ngTemplateOutlet="row; context: { $implicit: all, all: true }" />
            </div>
          }
          @if (named().length) {
            <div class="heading">
              <span class="heading__title" id="ws-division-switcher-heading">{{ 'shell.division_switcher.heading' | transloco }}</span>
              <span class="heading__rule" aria-hidden="true"></span>
            </div>
            @if (showSearch()) {
              <paper-input
                class="search"
                type="search"
                size="sm"
                prefix="search"
                clearable="auto"
                autocomplete="off"
                data-testid="division-search"
                [placeholder]="'shell.division_switcher.search' | transloco"
                [ariaLabel]="'shell.division_switcher.search' | transloco"
                [ngModel]="query()"
                (ngModelChange)="query.set($event ?? '')"
              />
            }
            @if (shown().length) {
              <div class="list" role="radiogroup" aria-labelledby="ws-division-switcher-heading">
                @for (division of shown(); track division.id) {
                  <ng-container *ngTemplateOutlet="row; context: { $implicit: division, all: false }" />
                }
              </div>
            } @else {
              <p class="state" role="status">{{ 'shell.division_switcher.no_match' | transloco }}</p>
            }
          }
        }
      </div>
    </ng-template>

    <!-- One row, for a division and for "All divisions": a radio in the
         panel's radiogroup, the selected state a wash, a tick and weight. -->
    <ng-template #row let-division let-all="all">
      <button
        type="button"
        role="radio"
        class="row"
        [class.is-on]="division.id === activeId()"
        [attr.aria-checked]="division.id === activeId()"
        [style.--ws-division-depth]="all ? 0 : division.depth"
        (click)="pick(division)"
      >
        <span class="thumb" aria-hidden="true">
          @if (all) {
            <paper-icon name="globe" [size]="18" />
          } @else {
            <span class="thumb__swatch" [style.background]="division.swatch"></span>
          }
        </span>
        @if (all) {
          <span class="info">
            <span class="name">{{ 'shell.division_switcher.all' | transloco }}</span>
            <span class="detail">{{ 'shell.division_switcher.all_hint' | transloco }}</span>
          </span>
        } @else {
          <span class="name">{{ division.name }}</span>
        }
        <span class="mark" aria-hidden="true">
          @if (division.id === activeId()) {
            <paper-icon name="check" [size]="14" />
          }
        </span>
      </button>
    </ng-template>
  `,
  styleUrl: './division-switcher.component.scss',
})
export class DivisionSwitcherComponent {
  protected readonly chrome = inject(ChromeService);
  private readonly analytics = inject(AnalyticsService);

  protected readonly positions = POSITIONS;
  private readonly _open = signal(false);
  protected readonly open = this._open.asReadonly();

  private readonly trigger = viewChild<ElementRef<HTMLElement>>('trigger');
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  protected readonly active = this.chrome.activeDivision;
  protected readonly activeId = this.chrome.activeDivisionId;

  /** The divisions the viewer administers, in the hierarchy's order. */
  protected readonly named = computed(() =>
    this.chrome.divisions().filter((d) => d.id !== ALL_DIVISIONS_ID),
  );
  /** The search, past `DIVISION_SEARCH_THRESHOLD` divisions. */
  protected readonly showSearch = computed(() => this.named().length > DIVISION_SEARCH_THRESHOLD);
  /** What the search holds; cleared each time the panel opens. */
  protected readonly query = signal('');
  /** The divisions the search leaves. */
  protected readonly shown = computed(() => filterDivisions(this.named(), this.query()));
  /** "All divisions", offered only to an admin of the whole estate. */
  protected readonly allRow = computed(
    () => this.chrome.divisions().find((d) => d.id === ALL_DIVISIONS_ID) ?? null,
  );

  protected toggle(): void {
    if (!this._open()) this.query.set('');
    this._open.update((v) => !v);
  }

  protected close(returnFocus = true): void {
    if (!this._open()) return;
    this._open.set(false);
    if (returnFocus) queueMicrotask(() => this.trigger()?.nativeElement.focus());
  }

  protected onAttach(): void {
    queueMicrotask(() => this.panel()?.nativeElement.focus());
  }

  protected onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  protected pick(division: Division): void {
    const from = this.activeId();
    if (division.id !== from) {
      this.chrome.setActiveDivision(division.id);
      this.analytics.capture(ANALYTICS_EVENTS.ADMIN_SHELL_DIVISION_SWITCHED, {
        to_all: division.id === ALL_DIVISIONS_ID,
        from_all: from === ALL_DIVISIONS_ID,
        depth: division.depth,
        division_count: this.named().length,
      });
    }
    this.close();
  }
}
